// src/components/CollectionItem.jsx
import React from "react";
import { Link } from "react-router-dom";
import GridImage from "./GridImage";

const formatMoney = (amount, currency = "GBP") => {
  const n = Number(amount ?? 0);
  const isWhole = Math.abs(n - Math.round(n)) < 1e-6;
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency,
    minimumFractionDigits: isWhole ? 0 : 2,
    maximumFractionDigits: isWhole ? 0 : 2,
  }).format(n);
};

function CollectionItem({ product, collectionHandle = "all" }) {
  // images: [{ url, width, height }, ...]
  const images =
    product?.images?.edges?.map((e) => e?.node).filter(Boolean) || [];
  const primaryImage = images[0] || null;
  const hoverImage = images[1] || null;

  // derive pricing across all variants
  const variants =
    (product?.variants?.edges || []).map((e) => e?.node).filter(Boolean) || [];

  // pick the cheapest variant (common e-com pattern)
  let displayPrice = null;
  let displayCompare = null;
  let currency = "GBP";

  for (const v of variants) {
    const p = v?.price?.amount != null ? Number(v.price.amount) : null;
    const c =
      v?.compareAtPrice?.amount != null
        ? Number(v.compareAtPrice.amount)
        : null;
    const cur = v?.price?.currencyCode || "GBP";
    if (p == null) continue;
    if (displayPrice == null || p < displayPrice) {
      displayPrice = p;
      displayCompare = c ?? null;
      currency = cur;
    }
  }

  const isOnSale =
    displayCompare != null &&
    displayPrice != null &&
    displayCompare > displayPrice;

  // Sold-out logic: trust Shopify's product flag
  const isSoldOut = product?.availableForSale === false;

  // NEW: use SEO handle instead of numeric ID
  const handle = product?.handle;

  // Fallback: if there's somehow no handle, don't generate a broken link
  const productUrl = handle
    ? `/collections/${collectionHandle}/products/${handle}`
    : "#";

  return (
    <div className="p-2 sm:p-6 flex flex-col items-center">
      <Link to={productUrl} className="block w-full md:w-72">
        <div className="relative w-full overflow-hidden">
          {/* Maintain aspect ratio so the grid doesn't jump */}
          <div style={{ aspectRatio: "4 / 5" }}>
            {/* SALE badge */}
            {isOnSale && (
              <span
                className="absolute top-3 left-3 z-10 rounded-full px-2 py-1 text-xs tracking-wide bg-blue-700 text-white"
                aria-label="On sale"
              >
                SALE
              </span>
            )}

            {/* Main image */}
            {primaryImage && (
              <GridImage
                node={primaryImage}
                alt={product?.title || "Product"}
                className="w-full h-full"
              />
            )}

            {/* Hover image layer */}
            {hoverImage && (
              <div className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-200">
                <GridImage
                  node={hoverImage}
                  alt={`${product?.title || "Product"} - Hover`}
                  className="w-full h-full"
                />
              </div>
            )}
          </div>
        </div>
      </Link>

      {/* Product Name and Price */}
      <div className="mt-4 text-center italic">
        <h3 className="text-md">{product?.title}</h3>

        {/* Price */}
        <div className="text-sm">
          {displayPrice != null ? (
            isOnSale ? (
              <div className="space-x-2">
                <span className="line-through opacity-60">
                  {formatMoney(displayCompare, currency)}
                </span>
                <span>{formatMoney(displayPrice, currency)}</span>
              </div>
            ) : (
              <span>{formatMoney(displayPrice, currency)}</span>
            )
          ) : null}
        </div>

        {/* Sold out label */}
        {isSoldOut && (
          <div className="mt-1 text-sm italic text-gray-500 opacity-70">
            [Sold out]
          </div>
        )}
      </div>
    </div>
  );
}

export default CollectionItem;
